import { Box, IconButton, makeStyles, Tooltip, Typography, useTheme } from '@material-ui/core';
import React, { useCallback } from 'react';
import { required, useInput, useTranslate } from 'react-admin';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import { Form } from 'antd';
import { Add } from '@material-ui/icons';
import CurrencySelect from './CurrencySelect';
import EditableCurrenciesTable from './EditableCurrenciesTable';
import MonthlyRoyaltyInput from './MonthlyRoyaltyInput';
import MktRoyaltyInput from './MktRoyaltyInput';
import CurrencyModeSelect from './CurrencyModeSelect';
import { CurrencyDisplayModeEnum } from '../../../../../constant';
import QuickAction from './QuickAction';
import { useHasCreateInvoicePermission } from '../../../../group/components/utils';

const CurrenciesConfig = () => {
  const theme = useTheme();
  const classes = useStyles();
  const t = useTranslate();
  const {
    meta: { error, touched, submitError },
    input: { onChange: handleCurrenciesChange, value: currencies = [] },
  } = useInput({
    source: 'currenciesConfig',
    validate: required(t('ra.validation.required')),
  });

  const showError = (submitError || error) && touched;

  const onChange = useCallback(
    (type, data) => {
      if (type === 'delete') {
        handleCurrenciesChange(currencies.filter((currency) => currency.id !== data.id));
      } else if (type === 'add') {
        handleCurrenciesChange([data, ...currencies]);
      } else if (type === 'update') {
        handleCurrenciesChange(
          currencies.map((currency) =>
            currency.id === data.id
              ? {
                  ...currency,
                  ...data,
                }
              : currency,
          ),
        );
      } else if (type === 'replace') {
        handleCurrenciesChange(data);
      }
    },
    [handleCurrenciesChange, currencies],
  );

  return (
    <Box
      mb={4}
      borderRadius={8}
      px={3.5}
      py={3.5}
      border={`1px solid ${showError ? theme.palette.error.main : theme.palette.grey[300]}`}
      color={showError ? theme.palette.error.main : undefined}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={4}>
        <Box
          display="flex"
          alignItems="center"
          sx={{
            gap: 4,
          }}
        >
          <Typography variant="h5" className={classes.label}>
            {t('resources.brand.fields.currencies')}
          </Typography>
          <Tooltip title={t('resources.brand.customRoyaltyTooltip')}>
            <IconButton size="small" color={showError ? theme.palette.error.main : undefined}>
              <InfoIcon className={classes.infoIcon} />
            </IconButton>
          </Tooltip>
        </Box>
        <QuickAction onChange={onChange} currencies={currencies} />
      </Box>
      <CurrencyTable onChange={onChange} currencies={currencies} className={classes.currencyInput} />
      <Box display={showError ? 'flex' : 'none'} marginTop="3px" error={!!showError} key={`helperText-${+showError}`}>
        {error || submitError || ' '}
      </Box>
    </Box>
  );
};

export default CurrenciesConfig;

const CurrencyTable = ({ onChange, currencies }) => {
  const [form] = Form.useForm();
  const classes = useStyles();
  const hasCreateInvoicePermission = useHasCreateInvoicePermission();

  const handleAddCurrency = ({ currency, mktRoyalty, monthlyRoyalty, mode }) => {
    onChange('add', {
      id: currency.id,
      name: currency.name,
      isThinEnabled: currency.isThinEnabled,
      mktRoyalty,
      monthlyRoyalty,
      mode: mode || CurrencyDisplayModeEnum.NORMAL,
    });
    form.resetFields(['currency', 'monthlyRoyalty', 'mktRoyalty', 'mode']);
  };

  return (
    <Box>
      <Form form={form} onFinish={handleAddCurrency}>
        <Box
          display="flex"
          alignItems="center"
          sx={{
            gap: 8,
          }}
        >
          <CurrencySelect className={classes.currencyInput} />
          {hasCreateInvoicePermission && (
            <>
              <MonthlyRoyaltyInput />
              <MktRoyaltyInput />
            </>
          )}
          <CurrencyModeSelect />
          <Form.Item>
            <IconButton
              variant="contained"
              disableTouchRipple
              disableRipple
              className={classes.addButton}
              type="submit"
            >
              <Add />
            </IconButton>
          </Form.Item>
        </Box>
      </Form>
      <Box mt={1} mb={4}>
        <EditableCurrenciesTable currencies={currencies} onChange={onChange} />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles({
  label: {
    fontSize: 16,
    fontWeight: 600,
  },
  infoIcon: {
    width: 18,
    height: 18,
  },
  addButton: {
    width: 30,
    height: 30,
  },
  currencyInput: {
    minWidth: 200,
  },
});
